import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface PaywallState {
  allowance: number;
  used: number;
  pricePerRo: number;
}

interface PaywallContextValue {
  paywallState: PaywallState | null;
  openPaywall: (payload: PaywallState) => void;
  closePaywall: () => void;
}

const PaywallContext = createContext<PaywallContextValue | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export const PaywallProvider = ({ children }: Props) => {
  const [paywallState, setPaywallState] = useState<PaywallState | null>(null);

  const openPaywall = useCallback((payload: PaywallState) => {
    setPaywallState(payload);
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallState(null);
  }, []);

  const value = useMemo<PaywallContextValue>(
    () => ({ paywallState, openPaywall, closePaywall }),
    [paywallState, openPaywall, closePaywall]
  );

  return <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>;
};

export const usePaywallContext = (): PaywallContextValue => {
  const ctx = useContext(PaywallContext);
  if (!ctx) {
    throw new Error('usePaywallContext must be used within a PaywallProvider');
  }
  return ctx;
};

/**
 * Optional variant — returns `null` instead of throwing when no PaywallProvider is mounted.
 * Used by workflow-transition orchestration to stay a no-op passthrough in tests and any
 * non-shop routes that don't wrap in a provider, without forcing every caller to wrap.
 */
export const useOptionalPaywallContext = (): PaywallContextValue | null => {
  return useContext(PaywallContext) ?? null;
};
